import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { getStyleByColor } from '@/components/knowledge-graph/chronology-panel/styles';
import { EntityPicker } from '@/components/knowledge-graph/EntityPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { ChronologyEvent } from '@/hooks/useChronology';
import type { ChronologyTagGroupInfo, ChronologyTagInfo } from '@/hooks/useChronologyEditing';
import {
  type DocumentSearchResult,
  type ManualCreateBody,
  type PatchEventBody,
  searchDocuments,
  useChronologyTags,
  useEventEditing,
} from '@/hooks/useChronologyEditing';
import { type CanonicalEntity, useEntities } from '@/hooks/useEntities';
import { apiErrorCode, apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { formatRegionalDate } from '@/utils/date-utils';
import { removeTimestampPrefix } from '@/utils/file-utils';

interface EventEditDialogProps {
  matterGuid: string;
  /** Existing event to edit, or omit/null to open in create mode. */
  event?: ChronologyEvent | null;
  /** chronology_id the event belongs to (or will belong to in create mode). */
  chronologyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (opts?: { revalidate?: boolean; focusEventId?: string }) => void | Promise<void>;
}

type SourceDocument = {
  document_id: number;
  filename?: string | null;
  doc_date?: string | null;
};

type DropdownPosition = {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

const TAG_GROUP_LABEL_CLASSNAME = 'min-w-0 whitespace-normal break-words text-left leading-4';

function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function normalizeLabel(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function eventTagIds(event?: ChronologyEvent | null): string[] {
  return (event?.tags || [])
    .map(tag => tag.id)
    .filter(Boolean)
    .sort();
}

function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every(id => bs.has(id));
}

const MAX_EVENT_TAGS = 5;

/**
 * Slide-over edit form for a single chronology event. Covers description,
 * detail, temporal_expression, projection tags, entities, sources.
 *
 * Concurrency: PATCH includes `Idempotency-Key: <uuid>` so axios retries
 * don't create duplicate version rows server-side.
 */
export function EventEditDialog({
  matterGuid,
  event,
  chronologyId,
  open,
  onOpenChange,
  onSaved,
}: EventEditDialogProps) {
  const isCreate = !event;
  const { patchEvent, createManualEvent } = useEventEditing(matterGuid);
  const { tagGroups, tags: chronologyTags, isLoading: isLoadingTags } = useChronologyTags(matterGuid, chronologyId);
  const { entities: allEntities } = useEntities(matterGuid);
  const [sheetContentEl, setSheetContentEl] = useState<HTMLDivElement | null>(null);

  const [description, setDescription] = useState(event?.description || '');
  const [detail, setDetail] = useState(event?.detail || '');
  // Edit form shows the human-readable date, not raw EDTF. The backend
  // converts whatever the user types back to EDTF on save via a cheap LLM
  // call — so any natural-language tweak (e.g. "3 PM on 26 Jan 2026") is
  // accepted, and unchanged values round-trip cleanly.
  const initialTemporal = event?.temporal_display || event?.temporal_expression || '';
  const [temporal, setTemporal] = useState(initialTemporal);
  const [temporalError, setTemporalError] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(eventTagIds(event));
  const [busy, setBusy] = useState(false);
  const resetState = useMemo(
    () => ({
      description: event?.description || '',
      detail: event?.detail || '',
      temporal: event?.temporal_display || event?.temporal_expression || '',
      tagIds: eventTagIds(event),
    }),
    [event],
  );

  // Pending source/entity edits — committed on Save. Adds and removes are
  // independent: a "remove" hides an existing source from the UI; an "add"
  // queues a doc. Net result is sent through PATCH so the server can
  // delete + insert junctions in one transaction. The picker is doc-level;
  // each pending doc carries the rep proposition_id sent as add_proposition_ids.
  const [addedDocs, setAddedDocs] = useState<DocumentSearchResult[]>([]);
  const [addEntityIds, setAddEntityIds] = useState<string[]>([]);
  const [removeEntityNames, setRemoveEntityNames] = useState<string[]>([]);

  const addPropIds = useMemo(
    () => addedDocs.map(d => d.rep_proposition_id),
    [addedDocs],
  );

  // Reset form whenever the dialog opens (or the active event changes).
  useEffect(() => {
    if (!open) return;
    setDescription(resetState.description);
    setDetail(resetState.detail);
    setTemporal(resetState.temporal);
    setTemporalError(null);
    setSelectedTagIds(resetState.tagIds);
    setAddedDocs([]);
    setAddEntityIds([]);
    setRemoveEntityNames([]);
  }, [open, resetState]);

  const editedFieldNote = event?.is_locked
    ? 'Edited — your changes survive rebuilds and new evidence will continue to attach.'
    : null;

  const dirty = isCreate
    ? (description.trim().length > 0 || temporal.trim().length > 0
       || selectedTagIds.length > 0 || addedDocs.length > 0 || addEntityIds.length > 0)
    : (
        description !== (event?.description || '') ||
        detail !== (event?.detail || '') ||
        temporal !== initialTemporal ||
        !sameIdSet(selectedTagIds, eventTagIds(event)) ||
        addedDocs.length > 0 ||
        addEntityIds.length > 0 ||
        removeEntityNames.length > 0
      );

  // Map kept-on-event entity names → ids via the matter's entity list.
  // PATCH `remove_entity_ids` needs ids; the bulk read only surfaces names.
  const entityByName = useMemo(() => {
    const m = new Map<string, CanonicalEntity>();
    for (const e of allEntities) {
      m.set(normalizeLabel(e.name), e);
      for (const a of e.aliases) m.set(normalizeLabel(a), e);
    }
    return m;
  }, [allEntities]);

  const removeEntityKeys = useMemo(
    () => new Set(removeEntityNames.map(normalizeLabel)),
    [removeEntityNames],
  );

  const visibleEntities = useMemo(() => {
    return (event?.entities ?? event?.direct_entities ?? [])
      .filter(e => e.role !== 'asserting')
      .filter(e => !removeEntityKeys.has(normalizeLabel(e.name)));
  }, [event?.entities, event?.direct_entities, removeEntityKeys]);

  const existingSourceDocs = useMemo(() => {
    const docs = new Map<number, SourceDocument>();
    const addDoc = (doc: SourceDocument | null | undefined) => {
      if (!doc?.document_id) return;
      const existing = docs.get(doc.document_id);
      docs.set(doc.document_id, {
        document_id: doc.document_id,
        filename: existing?.filename || doc.filename || null,
        doc_date: existing?.doc_date || doc.doc_date || null,
      });
    };

    for (const claim of event?.claims || []) {
      for (const doc of claim.source_documents || []) addDoc(doc);
    }
    for (const doc of event?.direct_source_documents || []) addDoc(doc);

    return Array.from(docs.values());
  }, [event?.claims, event?.direct_source_documents]);

  const entityCandidates = useMemo(() => {
    const takenIds = new Set([
      ...visibleEntities
        .map(e => entityByName.get(normalizeLabel(e.name))?.id)
        .filter((id): id is string => !!id),
      ...removeEntityNames
        .map(name => entityByName.get(normalizeLabel(name))?.id)
        .filter((id): id is string => !!id),
      ...addEntityIds,
    ]);
    const takenKeys = new Set([
      ...visibleEntities.map(e => normalizeLabel(e.name)),
      ...removeEntityNames.map(normalizeLabel),
      ...addEntityIds.flatMap(id => {
        const entity = allEntities.find(candidate => candidate.id === id);
        return entity
          ? [normalizeLabel(entity.name), ...entity.aliases.map(normalizeLabel)]
          : [];
      }),
    ]);
    return allEntities.filter(entity => {
      if (takenIds.has(entity.id)) return false;
      const candidateKeys = [normalizeLabel(entity.name), ...entity.aliases.map(normalizeLabel)];
      return !candidateKeys.some(key => takenKeys.has(key));
    });
  }, [allEntities, visibleEntities, entityByName, addEntityIds, removeEntityNames]);

  const groupedTags = useMemo(() => {
    const byGroup = new Map<string, { group: ChronologyTagGroupInfo | null; items: ChronologyTagInfo[] }>();
    for (const group of tagGroups) {
      byGroup.set(group.id, { group, items: [] });
    }
    for (const tag of chronologyTags) {
      const groupId = tag.group_id || 'ungrouped';
      const existing = byGroup.get(groupId);
      if (existing) {
        existing.items.push(tag);
      } else {
        byGroup.set(groupId, { group: null, items: [tag] });
      }
    }
    return Array.from(byGroup.values())
      .filter(group => group.items.length > 0)
      .sort((a, b) => (a.group?.sort_order ?? 999) - (b.group?.sort_order ?? 999));
  }, [chronologyTags, tagGroups]);

  function toggleTag(tagId: string) {
    setSelectedTagIds(prev => {
      if (prev.includes(tagId)) return prev.filter(id => id !== tagId);
      if (prev.length >= MAX_EVENT_TAGS) {
        toast.error(`Select up to ${MAX_EVENT_TAGS} tags per event`);
        return prev;
      }
      return [...prev, tagId];
    });
  }

  function getTemporalErrorMessage(err: unknown): string | null {
    if (apiErrorCode(err) !== 'invalid_temporal_expression') return null;
    return apiErrorMessage(err);
  }

  function refreshAfterSave(opts?: { revalidate?: boolean; focusEventId?: string }) {
    void Promise.resolve(onSaved(opts)).catch((err) => {
      console.error('Failed to refresh chronology after event save', err);
    });
  }

  async function handleSave() {
    setTemporalError(null);
    if (!temporal.trim() || (isCreate && !description.trim())) {
      toast.error(isCreate ? 'Description and date are required' : 'Date is required');
      return;
    }
    if (isLoadingTags) {
      toast.error('Tags are still loading');
      return;
    }
    setBusy(true);
    try {
      if (isCreate) {
        const body: ManualCreateBody = {
          chronology_id: chronologyId,
          description: description.trim(),
          detail: detail.trim() || undefined,
          temporal_expression: temporal.trim(),
          tag_ids: selectedTagIds,
          proposition_ids: addPropIds.length ? addPropIds : undefined,
          entity_ids: addEntityIds.length ? addEntityIds : undefined,
        };
        const created = await createManualEvent(body);
        toast.success('Event created');
        onOpenChange(false);
        refreshAfterSave({ revalidate: true, focusEventId: created.id });
      } else {
        if (!event) return;
        // Resolve names → ids for entities scheduled to be removed. Names
        // that don't resolve are silently dropped (legacy aliases) — the
        // user-visible X disappears anyway via local state.
        const removeEntityIds = removeEntityNames
          .map(n => entityByName.get(n.toLowerCase())?.id)
          .filter((id): id is string => !!id);
        // Only send the date when the user touched it — otherwise the
        // backend would re-run the LLM converter on every save.
        const temporalChanged = temporal !== initialTemporal;
        const descriptionChanged = description !== (event.description || '');
        const detailChanged = detail !== (event.detail || '');
        const tagsChanged = !sameIdSet(selectedTagIds, eventTagIds(event));
        const body: PatchEventBody = {
          description: descriptionChanged ? description.trim() : undefined,
          detail: detailChanged ? detail.trim() : undefined,
          temporal_expression: temporalChanged ? temporal.trim() : undefined,
          tag_ids: tagsChanged ? selectedTagIds : undefined,
          add_proposition_ids: addPropIds.length ? addPropIds : undefined,
          add_entity_ids: addEntityIds.length ? addEntityIds : undefined,
          remove_entity_ids: removeEntityIds.length ? removeEntityIds : undefined,
        };
        await patchEvent(event.id, body, {
          idempotencyKey: newIdempotencyKey(),
        });
        toast.success('Event saved');
        onOpenChange(false);
        refreshAfterSave({
          revalidate: temporalChanged
            || tagsChanged
            || addPropIds.length > 0
            || addEntityIds.length > 0
            || removeEntityIds.length > 0,
        });
      }
    } catch (err) {
      const temporalMessage = getTemporalErrorMessage(err);
      if (temporalMessage) {
        setTemporalError(temporalMessage);
      } else {
        toast.error(
          apiErrorMessage(err)
            || `Failed to ${isCreate ? 'create' : 'save'} event`,
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent ref={setSheetContentEl} side="right" className="flex w-full flex-col sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{isCreate ? 'New event' : 'Edit event'}</SheetTitle>
          {isCreate && (
            <SheetDescription>
              Add a manual event to this chronology.
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-4 flex-1 overflow-y-auto [scrollbar-gutter:stable] pr-1 space-y-4">
          {editedFieldNote && (
            <div className="text-[11px] text-gray-500">{editedFieldNote}</div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="flex w-full rounded-sm border border-input bg-white px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-0 resize-none"
              placeholder="What happened?"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Detail</label>
            <textarea
              value={detail}
              onChange={e => setDetail(e.target.value)}
              rows={4}
              className="flex w-full rounded-sm border border-input bg-white px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-0 resize-none"
              placeholder="Optional context"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Date</label>
            <Input
              value={temporal}
              onChange={e => {
                setTemporal(e.target.value);
                if (temporalError) setTemporalError(null);
              }}
              placeholder="e.g. 3 PM on 26 January 2026"
              aria-invalid={!!temporalError}
              className={temporalError ? 'border-red-300 bg-red-50/40 text-red-900 placeholder:text-red-400 focus-visible:border-red-500' : undefined}
            />
            {temporalError ? (
              <p className="mt-1 text-[10px] text-red-600">{temporalError}</p>
            ) : (
              <p className="text-[10px] text-gray-500 mt-1">
                Plain English — Crimson normalises it on save. Be specific enough that the date is unambiguous (a year is required).
              </p>
            )}
          </div>

          <TagsEditor
            groupedTags={groupedTags}
            selectedTagIds={selectedTagIds}
            isLoading={isLoadingTags}
            maxTags={MAX_EVENT_TAGS}
            onToggle={toggleTag}
          />

          <EntitiesEditor
            visibleEntities={visibleEntities}
            addedEntityIds={addEntityIds}
            candidates={entityCandidates}
            allEntities={allEntities}
            portalContainer={sheetContentEl}
            onRemove={(name) => setRemoveEntityNames(prev =>
              prev.some(existing => normalizeLabel(existing) === normalizeLabel(name))
                ? prev
                : [...prev, name]
            )}
            onUnremove={(name) => setRemoveEntityNames(prev =>
              prev.filter(existing => normalizeLabel(existing) !== normalizeLabel(name))
            )}
            removeQueue={removeEntityNames}
            onAdd={(id) => setAddEntityIds(prev => prev.includes(id) ? prev : [...prev, id])}
            onUnadd={(id) => setAddEntityIds(prev => prev.filter(x => x !== id))}
          />

          <SourcesEditor
            matterGuid={matterGuid}
            existingAttachedDocs={existingSourceDocs}
            blockedDocs={existingSourceDocs}
            popoverContainer={sheetContentEl}
            addedDocs={addedDocs}
            onAdd={(doc) => {
              setAddedDocs(prev => prev.some(d => d.document_id === doc.document_id) ? prev : [...prev, doc]);
            }}
            onUnadd={(documentId) => {
              setAddedDocs(prev => prev.filter(d => d.document_id !== documentId));
            }}
          />
        </div>

        <div className="flex justify-end items-center pt-3 border-t border-gray-200">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                busy
                || !dirty
                || (isCreate && !description.trim())
                || !temporal.trim()
                || isLoadingTags
              }
            >
              {busy ? (isCreate ? 'Creating…' : 'Saving…') : (isCreate ? 'Create' : 'Save')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}


// ---------------------------------------------------------------------------
// Tags editor — grouped projection tags, multi-select.
// ---------------------------------------------------------------------------

function TagsEditor({
  groupedTags,
  selectedTagIds,
  isLoading,
  maxTags,
  onToggle,
}: {
  groupedTags: { group: ChronologyTagGroupInfo | null; items: ChronologyTagInfo[] }[];
  selectedTagIds: string[];
  isLoading: boolean;
  maxTags: number;
  onToggle: (tagId: string) => void;
}) {
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);
  const selected = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);
  const atTagLimit = selectedTagIds.length >= maxTags;
  const selectedTags = useMemo(
    () => groupedTags.flatMap(({ items }) => items.filter(tag => selected.has(tag.id))),
    [groupedTags, selected],
  );

  useEffect(() => {
    if (!openGroupKey) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-tag-trigger], [data-tag-dropdown]')) return;
      setOpenGroupKey(null);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [openGroupKey]);

  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-500">Tags</label>
      {isLoading ? (
        <div className="rounded-md border border-dashed border-gray-200 bg-gray-50/60 px-3 py-2 text-xs text-gray-500">Loading tags…</div>
      ) : groupedTags.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-200 bg-gray-50/60 px-3 py-2 text-xs text-gray-500">No projection tags configured.</div>
      ) : (
        <div className="space-y-2.5 rounded-md border border-gray-200 bg-white p-2.5 shadow-xs">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
            {groupedTags.map(({ group, items }) => {
              const groupKey = group?.id || items[0]?.group_id || 'ungrouped';
              const groupLabel = group?.label || 'Other';
              const style = getStyleByColor(group?.color);
              const selectedInGroup = items.filter(tag => selected.has(tag.id));
              const selectedCount = selectedInGroup.length;
              const open = openGroupKey === groupKey;

              return (
                <div key={groupKey} className="relative inline-flex max-w-full">
                  <button
                    type="button"
                    data-tag-trigger
                    onClick={() => setOpenGroupKey(current => current === groupKey ? null : groupKey)}
                    className={cn(
                      'inline-flex min-h-7 max-w-full items-center gap-1 rounded-sm border px-2 py-0.5 text-[11px] font-medium shadow-xs transition-colors',
                      style.pill,
                      'opacity-80 shadow-none hover:opacity-100',
                    )}
                    title={groupLabel}
                    aria-expanded={open}
                    aria-label={`Select ${groupLabel} tags`}
                  >
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} />
                    <span className={TAG_GROUP_LABEL_CLASSNAME}>{groupLabel}</span>
                    {selectedCount > 0 && (
                      <span className="ml-0.5 inline-flex min-w-[1rem] items-center justify-center rounded-full bg-gray-100/80 px-1 text-[10px] text-gray-500 tabular-nums">
                        {selectedCount}
                      </span>
                    )}
                    <ChevronDown className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-180')} />
                  </button>
                  {open && (
                    <div
                      data-tag-dropdown
                      className="absolute bottom-full left-0 z-50 mb-1.5 w-[280px] overflow-hidden rounded-md border border-stone-200 bg-white p-0 text-stone-950 shadow-md"
                    >
                      <div className="border-b border-gray-100 px-3 py-2">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', style.activeDot)} />
                          <p className="truncate text-xs font-semibold text-gray-700">{groupLabel}</p>
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto py-1">
                        {items.map(tag => {
                          const checked = selected.has(tag.id);
                          const disabled = !checked && atTagLimit;
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              disabled={disabled}
                              onClick={() => onToggle(tag.id)}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-50',
                                checked && 'bg-gray-50',
                                disabled && 'cursor-not-allowed opacity-45 hover:bg-white',
                              )}
                              title={disabled ? `Maximum ${maxTags} tags per event` : tag.description || tag.label}
                              aria-pressed={checked}
                            >
                              <span className={cn(
                                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border transition-colors',
                                checked ? 'border-gray-600 bg-gray-600' : 'border-gray-300',
                              )}>
                                {checked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={2.75} />}
                              </span>
                              <span className={cn('min-w-0 flex-1 truncate', checked ? 'text-gray-800' : 'text-gray-600')}>
                                {tag.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-x-1.5 gap-y-1.5 border-t border-gray-100 pt-2.5">
              {selectedTags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => onToggle(tag.id)}
                  className="inline-flex max-w-full items-center gap-1 rounded-sm border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-white"
                  title={`Remove ${tag.label}`}
                >
                  <span className="max-w-[180px] truncate">{tag.label}</span>
                  <X className="h-3 w-3 shrink-0 text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Entities editor — list visible entities with X to remove + reusable picker.
// ---------------------------------------------------------------------------

function EntitiesEditor({
  visibleEntities,
  addedEntityIds,
  candidates,
  allEntities,
  portalContainer,
  onRemove,
  onUnremove,
  removeQueue,
  onAdd,
  onUnadd,
}: {
  visibleEntities: { name: string; entity_type?: string | null; role?: string | null }[];
  addedEntityIds: string[];
  candidates: CanonicalEntity[];
  allEntities: CanonicalEntity[];
  portalContainer?: HTMLElement | null;
  onRemove: (name: string) => void;
  onUnremove: (name: string) => void;
  removeQueue: string[];
  onAdd: (id: string) => void;
  onUnadd: (id: string) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1 block">Entities</label>
      <div className="space-y-1">
        {visibleEntities.map((ent) => (
          <div key={ent.name} className="flex items-center justify-between text-xs px-2 py-1 rounded-sm bg-gray-50">
            <span className="truncate text-gray-700">{ent.name}</span>
            <button
              type="button"
              onClick={() => onRemove(ent.name)}
              className="text-gray-400 hover:text-red-600"
              aria-label={`Remove ${ent.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {addedEntityIds.map((id) => {
          const ent = allEntities.find(e => e.id === id);
          if (!ent) return null;
          return (
            <div key={id} className="flex items-center justify-between text-xs px-2 py-1 rounded-sm bg-gray-50 border border-dashed border-gray-300 text-gray-700">
              <span className="truncate">{ent.name} <span className="text-[10px] text-gray-400">· will add on save</span></span>
              <button type="button" onClick={() => onUnadd(id)} className="text-gray-400 hover:text-gray-700">
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
        {removeQueue.length > 0 && (
          <div className="text-[10px] text-amber-700">
            {removeQueue.length} pending remove — undo: {removeQueue.map(n => (
              <button type="button" key={n} onClick={() => onUnremove(n)} className="underline mr-1">{n}</button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-1.5">
        <EntityPicker
          candidates={candidates}
          onPick={(ent) => onAdd(ent.id)}
          triggerLabel="Add entity"
          triggerIcon={Plus}
          searchPlaceholder="Search entity to add…"
          showEntityIcon
          portalContainer={portalContainer}
        />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sources editor — current source documents (read-only for now) plus a
// source-doc autocomplete.
// (/matters/{matterGuid}/documents/chronology-source-search). Picks
// resolve to the doc's representative proposition_id, attached via PATCH
// `add_proposition_ids`. Segment-level picking will return as a future mode.
// ---------------------------------------------------------------------------

function SourcesEditor({
  matterGuid,
  existingAttachedDocs,
  blockedDocs,
  popoverContainer,
  addedDocs,
  onAdd,
  onUnadd,
}: {
  matterGuid: string;
  existingAttachedDocs: SourceDocument[];
  blockedDocs: SourceDocument[];
  popoverContainer?: HTMLElement | null;
  addedDocs: DocumentSearchResult[];
  onAdd: (doc: DocumentSearchResult) => void;
  onUnadd: (documentId: number) => void;
}) {
  const locale = useMatterLocale();
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DocumentSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);

  useEffect(() => {
    if (!picking || !dropdownPosition) return;
    queryInputRef.current?.focus();
  }, [picking, dropdownPosition]);

  // Debounced search.
  useEffect(() => {
    if (!picking) return;
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await searchDocuments(matterGuid, query);
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [matterGuid, query, picking]);

  const uniqueDocs = useMemo(() => {
    const seen = new Map<number, { document_id: number; filename?: string | null; doc_date?: string | null }>();
    for (const d of existingAttachedDocs) {
      if (!seen.has(d.document_id)) seen.set(d.document_id, d);
    }
    return Array.from(seen.values());
  }, [existingAttachedDocs]);

  const blockedDocIds = useMemo(
    () => new Set(blockedDocs.map(doc => doc.document_id)),
    [blockedDocs],
  );

  const takenDocIds = useMemo(
    () => new Set([
      ...blockedDocIds,
      ...uniqueDocs.map(doc => doc.document_id),
      ...addedDocs.map(doc => doc.document_id),
    ]),
    [blockedDocIds, uniqueDocs, addedDocs],
  );

  const availableResults = useMemo(
    () => results.filter(result => !takenDocIds.has(result.document_id)),
    [results, takenDocIds],
  );

  const updateDropdownPosition = useCallback(() => {
    const trigger = triggerRef.current;
    const container = popoverContainer;
    if (!trigger || !container) {
      setDropdownPosition(null);
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const padding = 12;
    const width = Math.min(512, Math.max(320, containerRect.width - padding * 2));
    const left = Math.min(
      Math.max(triggerRect.left - containerRect.left, padding),
      Math.max(padding, containerRect.width - width - padding),
    );
    const spaceBelow = containerRect.bottom - triggerRect.bottom - padding;
    const spaceAbove = triggerRect.top - containerRect.top - padding;
    const placeBelow = spaceBelow >= 180 || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(120, Math.min(224, (placeBelow ? spaceBelow : spaceAbove) - 8));

    setDropdownPosition({
      left,
      width,
      maxHeight,
      ...(placeBelow
        ? { top: triggerRect.bottom - containerRect.top + 6 }
        : { bottom: containerRect.bottom - triggerRect.top + 6 }),
    });
  }, [popoverContainer]);

  useEffect(() => {
    if (!picking) {
      setDropdownPosition(null);
      return;
    }

    updateDropdownPosition();
    const handleLayoutChange = () => updateDropdownPosition();
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('scroll', handleLayoutChange, true);
    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('scroll', handleLayoutChange, true);
    };
  }, [picking, updateDropdownPosition]);

  useEffect(() => {
    if (!picking) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setPicking(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [picking]);

  const displaySourceName = (filename?: string | null, fallback?: string) =>
    filename ? removeTimestampPrefix(filename) : (fallback || '');

  const renderSearchMeta = (doc: Pick<DocumentSearchResult, 'doc_type' | 'doc_date' | 'author'>) => {
    const parts = [
      doc.doc_type ? doc.doc_type.replace(/_/g, ' ') : null,
      doc.doc_date ? formatRegionalDate(doc.doc_date, locale) : null,
      doc.author?.trim() || null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  return (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1 block">Sources</label>
      <div className="space-y-1">
        {uniqueDocs.map((d) => (
          <div key={d.document_id} className="flex items-center justify-between text-xs px-2 py-1 rounded-sm bg-gray-50">
            <span className="truncate text-gray-700">
              {displaySourceName(d.filename, `Document ${d.document_id}`)}
              {d.doc_date && <span className="ml-1 text-gray-400">· {formatRegionalDate(d.doc_date, locale)}</span>}
            </span>
          </div>
        ))}
        {addedDocs.map((d) => (
          <div key={d.document_id} className="flex items-start gap-2 text-xs px-2 py-1 rounded-sm bg-gray-50 border border-dashed border-gray-300 text-gray-700">
            <div className="min-w-0 flex-1">
              <p className="truncate">{displaySourceName(d.file_name, `Document ${d.document_id}`)}</p>
              <p className="text-[10px] text-gray-400">
                {renderSearchMeta(d) ? `${renderSearchMeta(d)} · ` : ''}will attach on save
              </p>
            </div>
            <button type="button" onClick={() => onUnadd(d.document_id)} className="text-gray-400 hover:text-gray-700 mt-0.5">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setPicking(prev => !prev)}
        aria-expanded={picking}
        className="mt-1 flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700"
      >
        <Plus className="h-3 w-3" />
        Add source
      </button>
      {picking && popoverContainer && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="absolute z-100000 space-y-1.5 rounded-md border border-gray-200 bg-white p-1.5 shadow-lg"
          style={{
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            top: dropdownPosition.top,
            bottom: dropdownPosition.bottom,
            zIndex: 100000,
          }}
        >
          <div className="flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1.5">
            <Search className="h-3 w-3 text-gray-400 shrink-0" />
            <input
              ref={queryInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search filename, doc type, date, or author…"
              className="flex-1 text-xs bg-transparent outline-hidden placeholder:text-gray-400"
            />
            <button type="button" onClick={() => { setPicking(false); setQuery(''); setResults([]); }} className="text-gray-400 hover:text-gray-600">
              <X className="h-3 w-3" />
            </button>
          </div>
          {searching && <p className="text-[11px] text-gray-400 px-1">Searching…</p>}
          {!searching && query.length >= 2 && availableResults.length === 0 && (
            <p className="text-[11px] text-gray-400 px-1">No matches</p>
          )}
          {availableResults.length > 0 && (
            <div
              className="divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200 scrollbar"
              style={{ maxHeight: dropdownPosition.maxHeight }}
            >
              {availableResults.map(r => (
                <button
                  type="button"
                  key={r.document_id}
                  onClick={() => {
                    if (takenDocIds.has(r.document_id)) return;
                    onAdd(r);
                    setQuery('');
                    setResults([]);
                    setPicking(false);
                  }}
                  className="w-full text-left text-xs px-2.5 py-1.5 hover:bg-gray-50"
                >
                  <p className="truncate text-gray-800">{displaySourceName(r.file_name, `Document ${r.document_id}`)}</p>
                  {renderSearchMeta(r) && (
                    <p className="text-[10px] text-gray-400">{renderSearchMeta(r)}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>,
        popoverContainer,
      )}
    </div>
  );
}
